import { Loader2 } from 'lucide-react';
import { buttonVariants } from '@/components/ui';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useVoterDataQueue } from '@/hooks/queries';
import { formatRelativeTime } from '@/lib/utils';
import { VoterDataExportStatus } from './VoterDataExportStatus';
import { voterDataRecordsLabel } from './voterDataOrder';

/**
 * The platform admin's Voter data fulfilment queue: every Paid order across the subsidiary, the
 * longest-waiting first, with how far its automatic L2 export got. Each row opens the order in its own campaign (the `campaignId` deep link
 * switches the selected campaign), where the recipe and the fulfil form live. Platform-admin only —
 * the route guards it and the API 403s anyone else.
 */
export function VoterDataQueue() {
  const { data, isLoading, isError } = useVoterDataQueue();
  const items = data?.items ?? [];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Voter data to fulfil</h1>
        <p className="mt-1 text-muted-foreground">
          Paid voter lists, oldest first. Each exports from L2 automatically; one that is held or stuck
          starting needs you to check L2 and deliver it by hand.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading the queue…
        </div>
      ) : isError ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t load the fulfilment queue.
        </p>
      ) : items.length === 0 ? (
        <p data-testid="voter-data-queue-empty" className="text-sm text-muted-foreground">
          No paid orders are waiting. A voter list appears here once its customer pays.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campaign</TableHead>
              <TableHead>List</TableHead>
              <TableHead>Records</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Export</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} data-testid="voter-data-queue-row">
                <TableCell className="font-medium text-foreground">{item.campaignName}</TableCell>
                <TableCell>{item.sentence || 'Voter list'}</TableCell>
                <TableCell className="tabular-nums">
                  {voterDataRecordsLabel(item.records, item.onePerHousehold)}
                </TableCell>
                <TableCell className="text-muted-foreground">{formatRelativeTime(item.paidAt)}</TableCell>
                <TableCell className="max-w-xs">
                  <VoterDataExportStatus
                    state={item.exportState}
                    holdReason={item.exportHoldReason}
                    holdDetail={item.exportHoldDetail}
                  />
                </TableCell>
                <TableCell className="text-right">
                  {/* A plain link: the order lives under another campaign, and the ?campaignId= deep
                      link switches to it on arrival before its campaign-scoped reads run. */}
                  <a
                    href={`/app/work-orders/${item.id}?campaignId=${item.campaignId}`}
                    className={buttonVariants({ size: 'sm', variant: 'outline' })}
                  >
                    Fulfil
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
